import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Radio } from '@mui/material';
import type { Chat, HcpSearchResult , KolSearchResult , LocationItem  } from '@trinity/chat';
import { SearchQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { SelectLocation } from '@trinity/chat-components/components/Search/Location';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useBrowserLocation } from '@trinity/chat-components/hooks/useBrowserLocation';
import { BrowserLocationError } from './BrowserLocationError';
import { SmartRoutingHcpContext } from './context';
import { SelectHcp } from './KolSearch.Form';
import type { SmartRoutingAction } from './SmartRouting.Container';

type Props = {
  value: Chat.QueryState.SmartRouting;
  hcp: KolSearchResult | HcpSearchResult | null;
  disabled: boolean;
  dispatch: React.Dispatch<SmartRoutingAction>;
  onLocationSubmitted?: (startingLocation: Chat.QueryState.SmartRouting['startingLocation']) => Promise<void>;
};

function getHcpLocationData(selectedHcp: KolSearchResult | HcpSearchResult) {
  if ('id' in selectedHcp && selectedHcp.id !== undefined) {
    return {
      id: selectedHcp.id,
      latitude: selectedHcp.locationCoords.latitude,
      longitude: selectedHcp.locationCoords.longitude,
    };
  }
  if ('hcpIdentifier' in selectedHcp) {
    const hcp = selectedHcp;
    return {
      id: parseInt(hcp.hcpIdentifier, 10),
      latitude: hcp.primaryLocation?.lat,
      longitude: hcp.primaryLocation?.lon,
    };
  }
  return { id: undefined, latitude: undefined, longitude: undefined };
}

function buildSmartRoutingLocation(
  radioState: 'browser' | 'search' | 'hcp',
  browserLocation: ICoords | null,
  selectorState: LocationItem | null,
  selectedHcp: KolSearchResult | HcpSearchResult | null,
): Chat.QueryState.SmartRouting['startingLocation'] {
  if (radioState === 'browser' && browserLocation) {
    return {
      type: 'browser',
      latitude: browserLocation.latitude,
      longitude: browserLocation.longitude,
    };
  }
  if (radioState === 'search' && selectorState) {
    return {
      type: 'search',
      name: selectorState.name,
      latitude: selectorState.latitude,
      longitude: selectorState.longitude,
      id: selectorState.id,
    };
  }
  if (radioState === 'hcp' && selectedHcp) {
    const { id, latitude, longitude } = getHcpLocationData(selectedHcp);
    return {
      type: 'hcp',
      id,
      name: selectedHcp.name,
      latitude,
      longitude,
    };
  }
  return null;
}

function resolveHcpFromContext(props: Props, fetchedHcp: KolSearchResult | HcpSearchResult | null): KolSearchResult | HcpSearchResult | null {
  if (props.hcp) return props.hcp;
  if (props.value.kolProfile) return props.value.kolProfile;
  if (props.value.hcpId && fetchedHcp) return fetchedHcp;
  return null;
}

function computeSmartRoutingCanSubmit(disabled: boolean, radioState: string, browserLocation: ICoords | null, selectorState: LocationItem | null, selectedHcp: KolSearchResult | HcpSearchResult | null) {
  if (disabled) return false;
  if (radioState === 'browser' && browserLocation) return true;
  if (radioState === 'search' && selectorState) return true;
  if (radioState === 'hcp' && selectedHcp) return true;
  return false;
}

function SmartRoutingBrowserStatus({ state, browserLocation, locationError }: { state: string; browserLocation: ICoords | null; locationError: string | null }) {
  if (state === 'loading') return <div>Waiting for permission...</div>;
  if (state === 'success' && browserLocation) return <div>{browserLocation.longitude}, {browserLocation.latitude}</div>;
  if (state === 'error') return <BrowserLocationError errorMessage={locationError} />;
  return null;
}

function HcpSearchSelector({ isHcpSearch, props, radioState, hcpFromContext, selectedHcp, hcpInputValue, handleHcpInputFocus, handleHcpInputBlur, setHcpInputValue, handleHcpSelection, apiClient }: {
  isHcpSearch: boolean;
  props: Props;
  radioState: string;
  hcpFromContext: KolSearchResult | HcpSearchResult | null;
  selectedHcp: KolSearchResult | HcpSearchResult | null;
  hcpInputValue: string;
  handleHcpInputFocus: () => void;
  handleHcpInputBlur: () => void;
  setHcpInputValue: (v: string) => void;
  handleHcpSelection: (v: KolSearchResult | HcpSearchResult | null) => void;
  apiClient: any;
}) {
  if (isHcpSearch) {
    return (
      <SelectHcp<HcpSearchResult>
        disabled={props.disabled || radioState !== 'hcp' || !!hcpFromContext}
        formatOptionLabel={formatHcpOptionLabelForHcp}
        onInputFocus={handleHcpInputFocus}
        onInputBlur={handleHcpInputBlur}
        onInputChange={setHcpInputValue}
        onSelection={handleHcpSelection}
        isClearable={true}
        value={(selectedHcp as HcpSearchResult | null) ?? null}
        inputValue={selectedHcp ? undefined : hcpInputValue}
        queryFn={apiClient.search.searchHcpsByName}
        searchIndex={props.value.searchIndex}
        getOptionLabel={(option: HcpSearchResult) => option.name}
        getOptionValue={(option: HcpSearchResult) => option.hcpIdentifier}
      />
    );
  }
  return (
    <SelectHcp<KolSearchResult>
      disabled={props.disabled || radioState !== 'hcp' || !!hcpFromContext}
      formatOptionLabel={formatHcpOptionLabel}
      onInputFocus={handleHcpInputFocus}
      onInputBlur={handleHcpInputBlur}
      onInputChange={setHcpInputValue}
      onSelection={handleHcpSelection}
      isClearable={true}
      value={selectedHcp as KolSearchResult | null}
      inputValue={selectedHcp ? undefined : hcpInputValue}
    />
  );
}

export const SmartRoutingLocation = (props: Props) => {
  const { fetchedHcp } = useContext(SmartRoutingHcpContext);

  const isHcpSearch = !!props.value.searchIndex;

  const hcpFromContext = useMemo(
    () => resolveHcpFromContext(props, fetchedHcp),
    [props.value, props.hcp, fetchedHcp]
  );

  const [radioState, setRadioState] = useState<'browser' | 'search' | 'hcp'>(hcpFromContext ? 'hcp' : props.value?.startingLocation?.type);
  const [selectorState, setSelectorState] = useState<LocationItem | null>(props.value?.startingLocation?.type === 'search' ? props.value.startingLocation : null);
  const [browserLocation, setBrowserLocation] = useState<ICoords | null>(props.value?.startingLocation?.type === 'browser' ? props.value.startingLocation : null);
  const [selectedHcp, setSelectedHcp] = useState<KolSearchResult | HcpSearchResult | null>(hcpFromContext);
  const [hcpInputValue, setHcpInputValue] = useState<string>(hcpFromContext?.name || '');
  const [locationError, setLocationError] = useState<string | null>(null);
  const { isSupported, getLocation, state: browserLocationState } = useBrowserLocation({ onSuccess: setBrowserLocation, onError: setLocationError });

  useEffect(() => {
    if (radioState === 'browser' && !browserLocation) {
      getLocation();
    }
  }, [browserLocation, getLocation, radioState]);

  useEffect(() => {
    if (hcpFromContext && !selectedHcp) {
      setSelectedHcp(hcpFromContext);
      setHcpInputValue(hcpFromContext.name);
      setRadioState('hcp');
    }
  }, [hcpFromContext, selectedHcp]);

  const onSubmit = useCallback(async () => {
    const startingLocation = buildSmartRoutingLocation(radioState, browserLocation, selectorState, selectedHcp);

    if (startingLocation) {
      props.dispatch({
        type: 'submit-location',
        payload: { startingLocation },
      });
      if (props.onLocationSubmitted) {
        await props.onLocationSubmitted(startingLocation);
      }
    }
  }, [browserLocation, props, radioState, selectedHcp, selectorState]);

  const inputFocused = useRef(false);
  const handleHcpInputBlur = useCallback(() => {
    inputFocused.current = false;
  }, []);

  const handleHcpInputFocus = useCallback(() => {
    inputFocused.current = true;
  }, []);

  const handleHcpSelection = useCallback((v: KolSearchResult | HcpSearchResult | null) => {
    setSelectedHcp(v);
    if (!v) setHcpInputValue('');
  }, []);

  const canSubmit = useMemo(
    () => computeSmartRoutingCanSubmit(props.disabled, radioState, browserLocation, selectorState, selectedHcp),
    [browserLocation, props.disabled, radioState, selectedHcp, selectorState]
  );

  const apiClient = useChatApiClient();

  return (
    <div>
      <Copy>Welcome to the Smart Routing Assistant! I can help you plan your HCP site visits efficiently.</Copy>
      <Copy>First let's plan our where you'll be starting from.</Copy>
      <List>
        <RadioRow>
          <Radio size='small' disabled={!isSupported || props.disabled} checked={radioState === 'browser'} onClick={() => setRadioState('browser')} />
          <div>Use my current location</div>
          <SmartRoutingBrowserStatus state={browserLocationState} browserLocation={browserLocation} locationError={locationError} />
        </RadioRow>
        <RadioRow>
          <Radio size='small' checked={radioState === 'search'} disabled={props.disabled} onClick={() => setRadioState('search')} />
          <RadioCopy>I'll be near this location</RadioCopy>
          <SelectLocation
            styles={{
              container: (provided) => ({
                ...provided,
                width: 350,
              }),
            }}
            placeholder="Start typing an address"
            isDisabled={radioState !== 'search' || props.disabled}
            value={selectorState}
            onChange={setSelectorState} />
        </RadioRow>
        <RadioRow>
          <Radio size='small' checked={radioState === 'hcp'} disabled={props.disabled} onClick={() => setRadioState('hcp')} />
          <RadioCopy>Use HCP's location</RadioCopy>
          <HcpSelectContainer>
            <HcpSearchSelector
              isHcpSearch={isHcpSearch}
              props={props}
              radioState={radioState}
              hcpFromContext={hcpFromContext}
              selectedHcp={selectedHcp}
              hcpInputValue={hcpInputValue}
              handleHcpInputFocus={handleHcpInputFocus}
              handleHcpInputBlur={handleHcpInputBlur}
              setHcpInputValue={setHcpInputValue}
              handleHcpSelection={handleHcpSelection}
              apiClient={apiClient}
            />
          </HcpSelectContainer>
        </RadioRow>
      </List>
      <StyledSearchHcps disabled={!canSubmit} onClick={onSubmit} value="Search HCPs" />
    </div>
  );
};

function formatHcpOptionLabel(result: KolSearchResult) {
  const specialty = result.specialties?.[0];
  return (
    <div>
      {result.name}{specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {result.locationName ? <KolResultSecondaryText>{result.locationName}</KolResultSecondaryText> : null}
    </div>
  );
}

function formatHcpOptionLabelForHcp(result: HcpSearchResult) {
  const specialty = result.specialty?.trim();
  const affiliation = result.primaryAffiliationName && result.primaryAffiliationName !== 'N'
    ? result.primaryAffiliationName.trim()
    : null;
  return (
    <div>
      {result.name}{specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {affiliation ? <KolResultSecondaryText>{affiliation}</KolResultSecondaryText> : null}
    </div>
  );
}

const KolResultSecondaryText = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
  marginTop: 5,
  fontSize: 15,
}));
const KolOptionSubText = styled.span(({ theme }) => ({
  fontSize: 14,
  marginLeft: 5,
  color: theme.palette.gray.main,
}));


const RadioCopy = styled.div(({
  width: 165,
}));

const Copy = styled.div({
  marginBottom: 5,
});

const StyledSearchHcps = styled(SearchQueryHint)({
  marginTop: 10,
});

const List = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const RadioRow = styled.div(() => ({
  display: 'flex',
  gap: 10,
  alignItems: 'center',
}));

const HcpSelectContainer = styled.div({
  width: 350,
});
